// Rewards System Constants and Configuration
// UPDATED TOKENOMICS: XP-First, BURNZ-Second Economy
// Philosophy: "Burnz Union converts broken Web3 engagement into sustainable activity, revenue, and reputation."

// ========== ECONOMY LAYERS ==========
// Layer 1: ENGAGEMENT → XP (non-transferable, slow decay)
// Layer 2: ACCESS → $BURNZ (unlocks, boosts, gating)
// Layer 3: STATUS → Prestige + Badges (reputation, voting)
// Layer 4: MONEY → ADA / Partner payments (real revenue)

// ========== LEVEL SYSTEM ==========
export const LEVEL_CONFIG = {
  baseXP: 1000,        // XP needed for level 2
  multiplier: 1.5,     // Each level needs 1.5x more XP
  maxLevel: 100,
  xpDecayRate: 0.02,   // 2% XP decay per week of inactivity
  xpDecayGracePeriod: 7, // Days before decay starts
};

// Calculate XP needed for a specific level
export const getXPForLevel = (level) => {
  if (level <= 1) return 0;
  return Math.floor(LEVEL_CONFIG.baseXP * Math.pow(LEVEL_CONFIG.multiplier, level - 2));
};

// Calculate total XP needed to reach a level
export const getTotalXPForLevel = (level) => {
  let total = 0;
  for (let i = 2; i <= level; i++) {
    total += getXPForLevel(i);
  }
  return total;
};

// Get current level from total XP
export const getLevelFromXP = (totalXP) => {
  let level = 1;
  let xpNeeded = 0;
  while (xpNeeded <= totalXP && level < LEVEL_CONFIG.maxLevel) {
    level++;
    xpNeeded += getXPForLevel(level);
  }
  return level - 1;
};

// ========== XP TO BURNZ CONVERSION ==========
// This is the CONTROL LEVER for the economy
export const CONVERSION_CONFIG = {
  baseRate: 200,          // 200 XP = 1 BURNZ (was 10:1, now much stricter)
  burnCost: 0.5,          // Must burn 0.5 BURNZ per 1 BURNZ claimed (50% burn cost)
  minXPToConvert: 10000,  // Minimum 10,000 XP required to convert
  cooldownDays: 7,        // Can only convert once per week
  tierGates: {            // Minimum tier to unlock conversion
    rookie: false,        // Cannot convert
    bronze: false,        // Cannot convert
    silver: true,         // Unlocks at Silver (Level 25+)
    gold: true,
    diamond: true,
    legendary: true,
    mythic: true,
  },
  tierBonuses: {          // Better conversion rates at higher tiers
    silver: 1.0,          // 200:1
    gold: 1.1,            // ~182:1
    diamond: 1.25,        // 160:1
    legendary: 1.5,       // ~133:1
    mythic: 2.0,          // 100:1
  },
  tierBurnReduction: {    // Lower burn costs at higher tiers
    silver: 0.50,         // 50% burn cost
    gold: 0.45,           // 45% burn cost
    diamond: 0.40,        // 40% burn cost
    legendary: 0.30,      // 30% burn cost
    mythic: 0.20,         // 20% burn cost (most efficient)
  },
};

// Calculate BURNZ earned from XP conversion
export const calculateConversion = (xp, tier) => {
  if (!CONVERSION_CONFIG.tierGates[tier]) return { burnzEarned: 0, burnzCost: 0, eligible: false };
  const bonus = CONVERSION_CONFIG.tierBonuses[tier] || 1.0;
  const burnReduction = CONVERSION_CONFIG.tierBurnReduction[tier] || 0.5;
  const burnzEarned = Math.floor((xp / CONVERSION_CONFIG.baseRate) * bonus);
  const burnzCost = Math.ceil(burnzEarned * burnReduction);
  return { burnzEarned, burnzCost, eligible: xp >= CONVERSION_CONFIG.minXPToConvert };
};

// ========== PRESTIGE TIERS ==========
// UPDATED: High tiers get EFFICIENCY bonuses, not raw BURNZ multipliers
export const PRESTIGE_TIERS = [
  { 
    id: 'rookie', 
    name: 'Rookie', 
    minLevel: 1, 
    maxLevel: 9, 
    icon: '🌱', 
    color: 'gray', 
    gradient: 'from-gray-500 to-gray-700', 
    benefits: ['Access to basic quests', 'Standard XP rates'],
    xpBonus: 0,
    burnzBonus: 0,
    votingPower: 1,
    canConvertXP: false,
  },
  { 
    id: 'bronze', 
    name: 'Bronze', 
    minLevel: 10, 
    maxLevel: 24, 
    icon: '🥉', 
    color: 'orange', 
    gradient: 'from-orange-600 to-orange-800', 
    benefits: ['+5% XP bonus', 'Bronze badge', 'Access to community pools'],
    xpBonus: 0.05,
    burnzBonus: 0,
    votingPower: 1,
    canConvertXP: false,
  },
  { 
    id: 'silver', 
    name: 'Silver', 
    minLevel: 25, 
    maxLevel: 49, 
    icon: '🥈', 
    color: 'slate', 
    gradient: 'from-slate-400 to-slate-600', 
    benefits: ['+10% XP bonus', 'XP→BURNZ conversion unlocked', 'Silver badge', 'Priority quest access'],
    xpBonus: 0.10,
    burnzBonus: 0, // No direct BURNZ bonus
    votingPower: 1,
    canConvertXP: true,
  },
  { 
    id: 'gold', 
    name: 'Gold', 
    minLevel: 50, 
    maxLevel: 74, 
    icon: '🥇', 
    color: 'yellow', 
    gradient: 'from-yellow-500 to-yellow-700', 
    benefits: ['+20% XP bonus', '+10% conversion rate', 'Gold badge', 'Exclusive quests', 'Voting power x2'],
    xpBonus: 0.20,
    burnzBonus: 0, // Efficiency via conversion, not direct
    votingPower: 2,
    canConvertXP: true,
  },
  { 
    id: 'diamond', 
    name: 'Diamond', 
    minLevel: 75, 
    maxLevel: 89, 
    icon: '💎', 
    color: 'cyan', 
    gradient: 'from-cyan-400 to-blue-600', 
    benefits: ['+35% XP bonus', '+25% conversion rate', '-10% burn fees', 'Early access features', 'Voting power x3'],
    xpBonus: 0.35,
    burnzBonus: 0,
    votingPower: 3,
    canConvertXP: true,
  },
  { 
    id: 'legendary', 
    name: 'Legendary', 
    minLevel: 90, 
    maxLevel: 99, 
    icon: '👑', 
    color: 'purple', 
    gradient: 'from-purple-500 to-pink-600', 
    benefits: ['+50% XP bonus', '+50% conversion rate', '-20% burn fees', 'Custom profile frame', 'Voting power x5'],
    xpBonus: 0.50,
    burnzBonus: 0,
    votingPower: 5,
    canConvertXP: true,
  },
  { 
    id: 'mythic', 
    name: 'Mythic', 
    minLevel: 100, 
    maxLevel: 100, 
    icon: '🔥', 
    color: 'red', 
    gradient: 'from-red-500 via-orange-500 to-yellow-500', 
    benefits: ['+100% XP bonus', '2x conversion rate', '-30% burn fees', 'Animated profile', 'Governance rights', 'Revenue share'],
    xpBonus: 1.00,
    burnzBonus: 0,
    votingPower: 10,
    canConvertXP: true,
  },
];

export const getPrestigeTier = (level) => {
  return PRESTIGE_TIERS.find(tier => level >= tier.minLevel && level <= tier.maxLevel) || PRESTIGE_TIERS[0];
};

// ========== EARNING RULES - XP FIRST ==========
// 90% of actions → XP, Only MILESTONES → $BURNZ
export const EARNING_RULES = {
  // Quest completions - XP ONLY
  quest_task: { xp: 100, burnz: 0, description: 'Complete a quest task' },
  quest_full_completion: { xp: 250, burnz: 0, description: 'Complete ALL tasks in a quest' },
  first_quest_daily: { xp: 50, burnz: 0, description: 'First quest of the day bonus' },
  
  // NFT Actions - XP ONLY (recovery/closure, not farming)
  nft_register: { xp: 50, burnz: 0, description: 'Register an NFT to a pool' },
  nft_dump: { xp: 300, burnz: 0, description: 'Dump an NFT (let go)', cooldownHours: 24, maxPerDay: 5 },
  first_dump: { xp: 500, burnz: 0, description: 'First ever dump bonus (one-time)' },
  
  // Social Actions - XP ONLY
  daily_login: { xp: 25, burnz: 0, description: 'Daily login' },
  streak_7_days: { xp: 200, burnz: 0, description: '7-day streak bonus' },
  streak_30_days: { xp: 1000, burnz: 0, description: '30-day streak bonus' },
  referral: { xp: 500, burnz: 0, description: 'Refer a new user' },
  referral_activity: { xp: 100, burnz: 0, description: 'Referred user completes first quest' },
  
  // Team Actions - XP ONLY (replaces pools, no voting)
  team_join: { xp: 100, burnz: 0, description: 'Join a team' },
  team_weekly_active: { xp: 50, burnz: 0, description: 'Active team member bonus (weekly)' },
  team_burn_contribution: { xp: 25, burnz: 0, description: 'Contribute to team burn total' },
  team_milestone: { xp: 500, burnz: 0, description: 'Team reaches new tier' },
  
  // MILESTONE REWARDS - ONLY WAY TO EARN BURNZ DIRECTLY
  level_10_milestone: { xp: 0, burnz: 25, description: 'Reach Level 10' },
  level_25_milestone: { xp: 0, burnz: 50, description: 'Reach Level 25 (unlock conversion)' },
  level_50_milestone: { xp: 0, burnz: 100, description: 'Reach Level 50' },
  level_75_milestone: { xp: 0, burnz: 200, description: 'Reach Level 75' },
  level_100_milestone: { xp: 0, burnz: 500, description: 'Reach Level 100 (Mythic)' },
  
  // Seasonal/Events - XP ONLY (no BURNZ)
  event_participation: { xp: 150, burnz: 0, description: 'Participate in seasonal event' },
  
  // ========== LEADERBOARD - HARDENED CAPS ==========
  // Primary rewards: XP + Badges + Prestige
  // BURNZ: SEASONAL ONLY, NON-STACKING, HARD CAPPED
  // Real prizes: conversion efficiency, prestige, early access
  
  // Individual Leaderboard - XP + Badges (NO direct BURNZ)
  leaderboard_rank_1: { xp: 5000, burnz: 0, description: 'Finish #1 weekly', badge: 'weekly_champion', conversionBonus: 0.05 },
  leaderboard_rank_2: { xp: 3500, burnz: 0, description: 'Finish #2 weekly', badge: 'weekly_elite' },
  leaderboard_rank_3: { xp: 2500, burnz: 0, description: 'Finish #3 weekly', badge: 'weekly_contender' },
  leaderboard_top_10: { xp: 1500, burnz: 0, description: 'Finish top 10 weekly', badge: 'weekly_top10' },
  leaderboard_top_50: { xp: 750, burnz: 0, description: 'Finish top 50 weekly' },
  leaderboard_top_100: { xp: 300, burnz: 0, description: 'Finish top 100 weekly' },
  
  // SEASONAL Leaderboard - LIMITED BURNZ (once per season, non-stacking)
  seasonal_rank_1: { xp: 10000, burnz: 250, description: 'Season champion', badge: 'season_champion', seasonal: true, nonStacking: true },
  seasonal_rank_2: { xp: 7500, burnz: 150, description: 'Season runner-up', badge: 'season_elite', seasonal: true, nonStacking: true },
  seasonal_rank_3: { xp: 5000, burnz: 100, description: 'Season top 3', badge: 'season_podium', seasonal: true, nonStacking: true },
  seasonal_top_10: { xp: 2500, burnz: 50, description: 'Season top 10', seasonal: true, nonStacking: true },
  seasonal_top_50: { xp: 1000, burnz: 0, description: 'Season top 50', seasonal: true },
};

// ========== DUMP ZONE SAFEGUARDS ==========
export const DUMP_SAFEGUARDS = {
  maxDumpsPerDay: 5,
  cooldownHours: 24,
  diminishingReturns: [
    { count: 1, xpMultiplier: 1.0 },    // First dump: full XP
    { count: 2, xpMultiplier: 0.9 },    // 2nd: 90%
    { count: 3, xpMultiplier: 0.75 },   // 3rd: 75%
    { count: 4, xpMultiplier: 0.5 },    // 4th: 50%
    { count: 5, xpMultiplier: 0.25 },   // 5th: 25%
  ],
  nftMinAgeHours: 24,                   // NFT must be held 24h before dumping
  whitelistedCollections: true,         // Only approved collections
  weeklyDumpCap: 20,                    // Max 20 dumps per week per wallet
};

// ========== BURN TRACKING ==========
// Burns should feel like IMPACT, not accounting
export const BURN_EVENTS = {
  xp_conversion: { rate: 0.5, description: 'XP to BURNZ conversion', visible: true },
  partner_payment_ada: { rate: 0.10, description: 'Partner pays with ADA (10% of ADA value burned)', visible: true },
  partner_discount_burnz: { rate: 0.15, description: 'Partner burns BURNZ for discount', visible: true },
  premium_features: { rate: 0.05, description: 'Premium feature purchases', visible: true },
  fireboard_ads: { rate: 0.15, description: 'Advertising slot purchases', visible: true },
  team_creation: { rate: 0.10, description: 'New team creation fee (admin only)', visible: true },
  expired_rewards: { rate: 1.0, description: 'Unclaimed rewards (30d)', visible: false },
};

// Burn visibility messages
export const BURN_MESSAGES = {
  conversion: (amount) => `🔥 You burned ${amount} BURNZ to claim your rewards. Supply reduced!`,
  milestone: (amount) => `🔥 ${amount} BURNZ permanently removed from circulation!`,
  total: (percent) => `You've helped reduce total supply by ${percent}%`,
  leaderboard: 'Top Burners this month',
};

// ========== BADGES ==========
export const BADGE_CATEGORIES = {
  achievement: { name: 'Achievements', icon: '🏆' },
  milestone: { name: 'Milestones', icon: '🎯' },
  social: { name: 'Social', icon: '👥' },
  special: { name: 'Special', icon: '⭐' },
  seasonal: { name: 'Seasonal', icon: '🎄' },
  burner: { name: 'Burner', icon: '🔥' },  // NEW: Burn-focused badges
};

export const BADGES = [
  // Achievement badges
  { id: 'first_flame', name: 'First Flame', icon: '🔥', category: 'achievement', description: 'Complete your first quest', rarity: 'common', xp_bonus: 100 },
  { id: 'quest_master', name: 'Quest Master', icon: '⚔️', category: 'achievement', description: 'Complete 50 quests', rarity: 'rare', xp_bonus: 500 },
  { id: 'quest_legend', name: 'Quest Legend', icon: '🗡️', category: 'achievement', description: 'Complete 200 quests', rarity: 'epic', xp_bonus: 2000 },
  { id: 'letting_go', name: 'Letting Go', icon: '💀', category: 'achievement', description: 'Dump your first NFT', rarity: 'common', xp_bonus: 100 },
  { id: 'clean_slate', name: 'Clean Slate', icon: '🧹', category: 'achievement', description: 'Dump 10 NFTs', rarity: 'rare', xp_bonus: 500 },
  { id: 'phoenix', name: 'Phoenix Rising', icon: '🦅', category: 'achievement', description: 'Dump 50 NFTs', rarity: 'legendary', xp_bonus: 5000 },
  
  // Milestone badges
  { id: 'level_10', name: 'Rising Star', icon: '⭐', category: 'milestone', description: 'Reach level 10', rarity: 'common', xp_bonus: 200 },
  { id: 'level_25', name: 'Converter', icon: '🌟', category: 'milestone', description: 'Reach level 25 (unlock conversion)', rarity: 'rare', xp_bonus: 500 },
  { id: 'level_50', name: 'Veteran', icon: '💫', category: 'milestone', description: 'Reach level 50', rarity: 'epic', xp_bonus: 1500 },
  { id: 'level_75', name: 'Elite', icon: '✨', category: 'milestone', description: 'Reach level 75', rarity: 'legendary', xp_bonus: 3000 },
  { id: 'level_100', name: 'Mythic Hero', icon: '🔱', category: 'milestone', description: 'Reach level 100', rarity: 'mythic', xp_bonus: 10000 },
  
  // Social badges
  { id: 'community_builder', name: 'Community Builder', icon: '👥', category: 'social', description: 'Join 5 community pools', rarity: 'common', xp_bonus: 150 },
  { id: 'influencer', name: 'Influencer', icon: '📣', category: 'social', description: 'Refer 10 users', rarity: 'rare', xp_bonus: 1000 },
  { id: 'voice_of_people', name: 'Voice of the People', icon: '🗳️', category: 'social', description: 'Vote 50 times', rarity: 'rare', xp_bonus: 400 },
  
  // Special badges
  { id: 'early_adopter', name: 'Early Adopter', icon: '🚀', category: 'special', description: 'Joined during beta', rarity: 'epic', xp_bonus: 1000 },
  { id: 'og', name: 'OG', icon: '👴', category: 'special', description: 'Account older than 1 year', rarity: 'epic', xp_bonus: 1500 },
  
  // Burner badges - NEW
  { id: 'first_burn', name: 'First Burn', icon: '🔥', category: 'burner', description: 'Complete your first XP conversion', rarity: 'common', xp_bonus: 100 },
  { id: 'supply_shrinker', name: 'Supply Shrinker', icon: '📉', category: 'burner', description: 'Burn 1,000 BURNZ total', rarity: 'rare', xp_bonus: 500 },
  { id: 'deflationist', name: 'Deflationist', icon: '💨', category: 'burner', description: 'Burn 10,000 BURNZ total', rarity: 'epic', xp_bonus: 2000 },
  { id: 'burn_legend', name: 'Burn Legend', icon: '☄️', category: 'burner', description: 'Burn 100,000 BURNZ total', rarity: 'legendary', xp_bonus: 10000 },
  { id: 'supply_destroyer', name: 'Supply Destroyer', icon: '🌋', category: 'burner', description: 'Top 10 all-time burner', rarity: 'mythic', xp_bonus: 25000 },
  
  // Seasonal badges
  { id: 'winter_warrior', name: 'Winter Warrior', icon: '❄️', category: 'seasonal', description: 'Complete Winter 2026 event', rarity: 'rare', xp_bonus: 750 },
  { id: 'summer_blazer', name: 'Summer Blazer', icon: '☀️', category: 'seasonal', description: 'Complete Summer 2026 event', rarity: 'rare', xp_bonus: 750 },
];

export const BADGE_RARITY = {
  common: { name: 'Common', color: 'gray', glow: 'none' },
  rare: { name: 'Rare', color: 'blue', glow: 'shadow-blue-500/30' },
  epic: { name: 'Epic', color: 'purple', glow: 'shadow-purple-500/40' },
  legendary: { name: 'Legendary', color: 'orange', glow: 'shadow-orange-500/50' },
  mythic: { name: 'Mythic', color: 'red', glow: 'shadow-red-500/60 animate-pulse' },
};

// ========== SEASONAL EVENTS ==========
// XP rewards, with OPTIONAL BURNZ conversion
export const CURRENT_SEASON = {
  id: 'winter_2026',
  name: 'Winter Blaze',
  theme: 'winter',
  icon: '❄️',
  startDate: '2026-01-01',
  endDate: '2026-02-28',
  description: 'Burn through the cold! Complete winter challenges for XP and exclusive rewards.',
  rewards: [
    { threshold: 2500, reward: 'Winter Warrior Badge', icon: '❄️', type: 'badge' },
    { threshold: 7500, reward: 'Ice Crown Profile Frame', icon: '👑', type: 'cosmetic' },
    { threshold: 15000, reward: '50 BURNZ (Claimable)', icon: '🔥', type: 'burnz', amount: 50 },
    { threshold: 30000, reward: 'Legendary NFT + 100 BURNZ', icon: '🎁', type: 'nft', burnz: 100 },
  ],
  challenges: [
    { id: 'w1', name: 'Daily Dedication', description: 'Log in 14 days total', xp: 750, progress: 0, target: 14 },
    { id: 'w2', name: 'Quest Blitz', description: 'Complete 30 quests', xp: 1500, progress: 0, target: 30 },
    { id: 'w3', name: 'Team Spirit', description: 'Be active in your team for 7 days', xp: 500, progress: 0, target: 7 },
    { id: 'w4', name: 'Closure', description: 'Dump 3 NFTs', xp: 1000, progress: 0, target: 3 },
    { id: 'w5', name: 'Burn Champion', description: 'Convert XP to BURNZ 3 times', xp: 2000, progress: 0, target: 3 },
  ],
  // HARD CAPPED leaderboard prizes - XP PRIMARY, BURNZ SEASONAL ONLY
  leaderboardPrizes: [
    { rank: '1st', xp: 10000, burnz: 250, badge: 'Season Champion', icon: '🥇', conversionBonus: 0.05, burnFeeReduction: 0.10 },
    { rank: '2nd', xp: 7500, burnz: 150, badge: 'Season Elite', icon: '🥈', conversionBonus: 0.03, burnFeeReduction: 0.05 },
    { rank: '3rd', xp: 5000, burnz: 100, badge: 'Season Veteran', icon: '🥉', burnFeeReduction: 0.03 },
    { rank: '4-10', xp: 2500, burnz: 50, icon: '🏅' },
    { rank: '11-50', xp: 1000, burnz: 0, icon: '🎖️' },
    { rank: '51-100', xp: 500, burnz: 0, icon: '📜' },
  ],
};

// ========== LEADERBOARD ==========
export const DEMO_LEADERBOARD = [
  { rank: 1, username: 'CryptoKing', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=king', xp: 458720, level: 87, tier: 'diamond', badges: 24, burnzBurned: 45000 },
  { rank: 2, username: 'NFTQueen', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=queen', xp: 412350, level: 82, tier: 'diamond', badges: 21, burnzBurned: 38000 },
  { rank: 3, username: 'DeFiDegen', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=degen', xp: 389200, level: 79, tier: 'diamond', badges: 19, burnzBurned: 32000 },
  { rank: 4, username: 'CardanoWhale', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=whale', xp: 345670, level: 74, tier: 'gold', badges: 18, burnzBurned: 28000 },
  { rank: 5, username: 'BurnzMaster', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=master', xp: 312400, level: 71, tier: 'gold', badges: 17, burnzBurned: 25000 },
  { rank: 6, username: 'MoonShot', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=moon', xp: 287650, level: 68, tier: 'gold', badges: 15, burnzBurned: 21000 },
  { rank: 7, username: 'DiamondHands', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=diamond', xp: 256890, level: 64, tier: 'gold', badges: 14, burnzBurned: 18000 },
  { rank: 8, username: 'ApeStrong', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=ape', xp: 234100, level: 61, tier: 'gold', badges: 13, burnzBurned: 15000 },
  { rank: 9, username: 'HODLer', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=hodl', xp: 198750, level: 57, tier: 'gold', badges: 12, burnzBurned: 12000 },
  { rank: 10, username: 'WenLambo', avatar: 'https://api.dicebear.com/7.x/bottts-neutral/svg?seed=lambo', xp: 178200, level: 54, tier: 'gold', badges: 11, burnzBurned: 9000 },
];

// ========== PARTNER PRICING ==========
// Partners pay ADA (real revenue) or BURN BURNZ for discounts
export const PARTNER_PRICING = {
  starter: {
    name: 'Starter',
    adaPrice: 500,
    burnzDiscount: 500000,  // Burn 500K BURNZ for 20% off
    discountPercent: 20,
    duration: '2 weeks',
    reach: '10-25K impressions',
    features: ['Basic quest creation', 'Standard analytics', 'Community support'],
  },
  growth: {
    name: 'Growth',
    adaPrice: 1500,
    burnzDiscount: 1500000,  // Burn 1.5M BURNZ for 25% off
    discountPercent: 25,
    duration: '4 weeks',
    reach: '50-100K impressions',
    features: ['Advanced quests', 'Priority support', 'Featured placement', 'Custom branding'],
  },
  enterprise: {
    name: 'Enterprise',
    adaPrice: 5000,
    burnzDiscount: 5000000,  // Burn 5M BURNZ for 30% off
    discountPercent: 30,
    duration: '8 weeks',
    reach: '250K+ impressions',
    features: ['Unlimited quests', 'Dedicated manager', 'API access', 'White-label options', 'Premium analytics'],
  },
};

// ========== MULTIPLIER CALCULATIONS ==========
export const calculateXPMultiplier = (level, streakDays = 0, hasSeasonPass = false) => {
  const tier = getPrestigeTier(level);
  let multiplier = 1;
  
  // Tier bonus (XP only, not BURNZ)
  multiplier += tier.xpBonus;
  
  // Streak bonus (capped at 30 days for +30%)
  const streakBonus = Math.min(streakDays, 30) * 0.01; // +1% per day, max 30%
  multiplier += streakBonus;
  
  // Season pass bonus
  if (hasSeasonPass) multiplier += 0.15; // Reduced from 25%
  
  return multiplier;
};

// DEPRECATED: No more direct BURNZ multipliers
// Use conversion efficiency instead
export const calculateBURNZMultiplier = (level) => {
  // This now returns conversion efficiency, not earning multiplier
  const tier = getPrestigeTier(level);
  return CONVERSION_CONFIG.tierBonuses[tier.id] || 1.0;
};

// ========== BURN STATS ==========
export const DEMO_BURN_STATS = {
  totalBurned: 12547832,
  burnedToday: 45678,
  burnedThisWeek: 287432,
  burnedThisMonth: 1234567,
  topBurners: [
    { username: 'BurnzMaster', burned: 125000, rank: 1 },
    { username: 'CryptoKing', burned: 98000, rank: 2 },
    { username: 'DeflationFan', burned: 87000, rank: 3 },
  ],
  burnRate: 2.3, // % of circulating supply burned
  userBurned: 4500, // Current user's total burns
  userBurnRank: 156,
  supplyReduction: 0.12, // User's contribution to supply reduction %
};

// ========== ECONOMY SUMMARY ==========
export const ECONOMY_PHILOSOPHY = {
  mission: "Burnz Union converts broken Web3 engagement into sustainable activity, revenue, and reputation.",
  principles: [
    "XP is the primary reward - abundant, non-transferable, decays",
    "BURNZ is scarce - earned through milestones and conversion only",
    "Burns are visible and emotional - users feel their impact",
    "High tiers get efficiency, not more emissions",
    "ADA is real revenue - BURNZ burns drive external buy pressure",
  ],
  layers: {
    engagement: { asset: 'XP', purpose: 'Progress, grind, fun' },
    access: { asset: 'BURNZ', purpose: 'Unlocks, boosts, gating' },
    status: { asset: 'Prestige + Badges', purpose: 'Reputation & voting' },
    money: { asset: 'ADA', purpose: 'Real revenue' },
  },
};
